import { Injectable } from '@angular/core'
import {
  ClearwaterTransaction,
  ExternalTransaction,
  ExternalTransactionStatus,
} from '@helios/common-api'
import {
  BulkTransactionModalMode,
  BulkTransactionsModalService,
  CreateTransactionsModalService,
  TransactionModalMode,
} from '@helios/create-transactions-modal'
import {
  ExternalFileFilter,
  ExternalFileLinkedPanel,
  RootState,
  securityMappingAction,
  SecurityMappingFilter,
  SecurityMappingLinkedPanel,
  setMatchedCwTransFilter,
  showRawDataAction,
} from '@helios/state-management'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { ExternalTransactionStatusKey } from '../models/external-transaction'
import { ExternalTransactionsActionResponse } from '../models/external-transactions-action.model'
import { ExternalTransactionsService } from './external-transactions.service'

@Injectable({
  providedIn: 'root',
})
export class ExternalTransactionsTaskService {
  constructor(
    private readonly createTransactionsModalService: CreateTransactionsModalService,
    private readonly modalService: NgbModal,
    private readonly store: Store<RootState>,
    private readonly transactionsService: ExternalTransactionsService,
    private readonly bulkTransactionModalService: BulkTransactionsModalService,
  ) {}

  openCashEntryModal(
    extTranId: string,
    status: ExternalTransactionStatus | undefined,
  ) {
    this.createTransactionsModalService.toggleCashEntryModal({
      modalMode: TransactionModalMode.CreateFromExtTran,
      transactionId: extTranId,
      externalTransactionStatus:
        ExternalTransactionStatus[status as ExternalTransactionStatusKey],
    })
  }
  openCashEntryBulkModal(
    extTranId: number[],
    bulkStatus: Map<number, ExternalTransactionStatus | undefined>,
  ) {
    this.bulkTransactionModalService.toggleCashEntryModal({
      modalMode: BulkTransactionModalMode.CreateFromExtTran,
      extTranId: extTranId,
      bulkStatus: bulkStatus,
    })
  }

  openTradeEntryModal(
    extTranId: string,
    status: ExternalTransactionStatus | undefined,
  ) {
    this.createTransactionsModalService.toggleTradeEntryModal({
      modalMode: TransactionModalMode.CreateFromExtTran,
      transactionId: extTranId,
      externalTransactionStatus:
        ExternalTransactionStatus[status as ExternalTransactionStatusKey],
    })
  }
  openTradeEntryBulkModal(
    extTranId: number[],
    bulkStatus: Map<number, ExternalTransactionStatus | undefined>,
  ) {
    this.bulkTransactionModalService.toggleTradeEntryModal({
      modalMode: BulkTransactionModalMode.CreateFromExtTran,
      extTranId: extTranId,
      bulkStatus: bulkStatus,
    })
  }

  openFXTradeModal(
    extTranId: string,
    status: ExternalTransactionStatus | undefined,
  ) {
    this.createTransactionsModalService.toggleFxSpotTradeModal({
      modalMode: TransactionModalMode.CreateFromExtTran,
      transactionId: extTranId,
      externalTransactionStatus:
        ExternalTransactionStatus[status as ExternalTransactionStatusKey],
    })
  }
  openFxTradeBulkModal(
    extTranId: number[],
    bulkStatus: Map<number, ExternalTransactionStatus | undefined>,
  ) {
    this.bulkTransactionModalService.toggleFxSpotTradeModal({
      modalMode: BulkTransactionModalMode.CreateFromExtTran,
      extTranId: extTranId,
      bulkStatus: bulkStatus,
    })
  }

  openCurrencyForwardModal(
    extTranId: string,
    status: ExternalTransactionStatus | undefined,
  ) {
    this.createTransactionsModalService.toggleFxForwardModal({
      modalMode: TransactionModalMode.CreateFromExtTran,
      transactionId: extTranId,
      externalTransactionStatus:
        ExternalTransactionStatus[status as ExternalTransactionStatusKey],
    })
  }
  openCurrencyForwardBulkModal(
    extTranId: number[],
    bulkStatus: Map<number, ExternalTransactionStatus | undefined>,
  ) {
    this.bulkTransactionModalService.toggleFxForwardModal({
      modalMode: BulkTransactionModalMode.CreateFromExtTran,
      extTranId: extTranId,
      bulkStatus: bulkStatus,
    })
  }

  openWireTransferModal(
    extTranId: string,
    status: ExternalTransactionStatus | undefined,
  ) {
    this.createTransactionsModalService.toggleCreateTransactionModal('WIRE', {
      modalMode: TransactionModalMode.CreateFromExtTran,
      transactionId: extTranId,
      externalTransactionStatus:
        ExternalTransactionStatus[status as ExternalTransactionStatusKey],
    })
  }
  openWireTransferBulkModal(
    extTranId: number[],
    bulkStatus: Map<number, ExternalTransactionStatus | undefined>,
  ) {
    this.bulkTransactionModalService.toggleWireTransferModal({
      modalMode: BulkTransactionModalMode.CreateFromExtTran,
      extTranId: extTranId,
      bulkStatus: bulkStatus,
    })
  }

  openSecurityTransferInBulkModal(
    extTranId: number[],
    bulkStatus: Map<number, ExternalTransactionStatus | undefined>,
  ) {
    this.bulkTransactionModalService.toggleSecurityTransferInModal({
      modalMode: BulkTransactionModalMode.CreateFromExtTran,
      extTranId: extTranId,
      bulkStatus: bulkStatus,
    })
  }

  openSecurityTransferOutBulkModal(
    extTranId: number[],
    bulkStatus: Map<number, ExternalTransactionStatus | undefined>,
  ) {
    this.bulkTransactionModalService.toggleSecurityTransferOutModal({
      modalMode: BulkTransactionModalMode.CreateFromExtTran,
      extTranId: extTranId,
      bulkStatus: bulkStatus,
    })
  }

  openSecurityTransferModal(
    extTranId: string,
    status: ExternalTransactionStatus | undefined,
  ) {
    this.createTransactionsModalService.toggleSecurityTransferModal({
      modalMode: TransactionModalMode.CreateFromExtTran,
      transactionId: extTranId,
      externalTransactionStatus:
        ExternalTransactionStatus[status as ExternalTransactionStatusKey],
    })
  }

  hold(
    extTrans: ExternalTransaction[],
  ): Promise<ExternalTransactionsActionResponse> {
    const extTransWithAvailableAction: ExternalTransaction[] = []
    extTrans.forEach(trans => {
      if (trans.availableActions.includes('hold')) {
        extTransWithAvailableAction.push(trans)
      }
    })
    return this.transactionsService
      .hold(extTransWithAvailableAction)
      .toPromise()
  }

  unhold(
    extTrans: ExternalTransaction[],
  ): Promise<ExternalTransactionsActionResponse> {
    const extTransWithAvailableAction: ExternalTransaction[] = []
    extTrans.forEach(trans => {
      if (trans.availableActions.includes('unhold')) {
        extTransWithAvailableAction.push(trans)
      }
    })
    return this.transactionsService
      .unhold(extTransWithAvailableAction)
      .toPromise()
  }

  ignore(
    extTrans: ExternalTransaction[],
  ): Promise<ExternalTransactionsActionResponse> {
    const extTransWithAvailableAction: ExternalTransaction[] = []
    extTrans.forEach(trans => {
      if (trans.availableActions.includes('ignore')) {
        extTransWithAvailableAction.push(trans)
      }
    })
    return this.transactionsService
      .ignore(extTransWithAvailableAction)
      .toPromise()
  }

  unignore(
    extTrans: ExternalTransaction[],
  ): Promise<ExternalTransactionsActionResponse> {
    const extTransWithAvailableAction: ExternalTransaction[] = []
    extTrans.forEach(trans => {
      if (trans.availableActions.includes('unignore')) {
        extTransWithAvailableAction.push(trans)
      }
    })
    return this.transactionsService
      .unignore(extTransWithAvailableAction)
      .toPromise()
  }

  unmatch(
    extTrans: ExternalTransaction[],
  ): Promise<ExternalTransactionsActionResponse> {
    return this.transactionsService
      .unmatch(extTrans.filter(tx => tx.availableActions.includes('unmatch')))
      .toPromise()
  }

  async match(
    extTrans: ExternalTransaction[],
    cwTrans: ClearwaterTransaction[],
  ): Promise<ExternalTransactionsActionResponse | undefined> {
    if (cwTrans.length === 0 || extTrans.length === 0) {
      return
    }

    return await this.transactionsService.match(extTrans, cwTrans).toPromise()
  }

  /**
   * Match action handler.
   */
  async manyToManyMatch(
    extTrans: ExternalTransaction[],
    cwTrans: ClearwaterTransaction[],
  ): Promise<ExternalTransactionsActionResponse | undefined> {
    if (cwTrans.length === 0 || extTrans.length === 0) {
      return
    }

    return this.transactionsService
      .manyToManyMatch(extTrans, cwTrans)
      .toPromise()
  }

  async matchUsingExternal(
    extTrans: ExternalTransaction[],
    cwTrans: ClearwaterTransaction[],
  ): Promise<ExternalTransactionsActionResponse> {
    const result = await this.transactionsService
      .matchUsingExternal(extTrans[0], cwTrans)
      .toPromise()
    return result
  }
  findMatchedTransaction(extTrans: ExternalTransaction[]) {
    this.store.dispatch(
      setMatchedCwTransFilter(
        extTrans
          .filter(x => x.status === ExternalTransactionStatus.MATCHED)
          .map(row => row.extTranId),
      ),
    )
  }

  openExternalFileViewer(extTran: ExternalTransaction) {
    const originId = extTran.originId
    if (originId === undefined) {
      return
    }

    this.store.dispatch(
      showRawDataAction({
        id: `${originId}`,
        accountId: extTran.accountId,
        reconDate: extTran.reconDate,
        filterText: extTran.externalSecIdentifier,
        panelName: ExternalFileLinkedPanel.EXT_TRANSACTION,
      } as ExternalFileFilter),
    )
  }

  openSecurityMappingPanel(extTran: ExternalTransaction) {
    this.store.dispatch(
      securityMappingAction({
        extTransactionId: extTran.extTranId,
        panelName: SecurityMappingLinkedPanel.EXT_TRANSACTION,
      } as SecurityMappingFilter),
    )
  }
}
